import theme from '@gaia/theme';
import {
    Box,
    type BoxProps,
    Divider,
    type DividerProps,
    Link,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {forwardRef} from 'react';

const PANEL_WIDTH = 275;

function ResponsivePanel({children}: BoxProps) {
    return (
        <Box
            sx={{
                maxWidth: PANEL_WIDTH,
                width: {
                    sm: PANEL_WIDTH,
                },
            }}
        >
            {children}
        </Box>
    );
}

function FullWidthDivider(props: DividerProps) {
    return (
        <Divider
            orientation='horizontal'
            sx={{
                marginTop: theme.spacing(4),
                marginBottom: theme.spacing(4),
                marginLeft: theme.spacing(-4),
                width: `calc(100% + ${theme.spacing(8)})`,
            }}
            {...props}
        />
    );
}

function VerticalDivider() {
    return (
        <Divider
            orientation='vertical'
            flexItem
            sx={{
                marginTop: 0,
                marginBottom: 0,
                marginLeft: theme.spacing(4),
                marginRight: theme.spacing(4),
                width: '1px',
            }}
        />
    );
}

export default forwardRef(function LoginFormContainer(
    {
        panelPrimary,
        panelSecondary,
    }: {
        panelPrimary: React.ReactNode;
        panelSecondary: React.ReactNode;
    },
    ref: React.Ref<HTMLDivElement>
) {
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

    const recaptchaAttribution = (
        <>
            <Typography variant='body2'>
                {' '}
                This site is protected by reCAPTCHA and the Google{' '}
                <Link href='https://policies.google.com/privacy'>Privacy Policy</Link> and{' '}
                <Link href='https://policies.google.com/terms'>Terms of Service</Link> apply.
            </Typography>
        </>
    );

    return (
        <div ref={ref} id='login-form-container'>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: {
                        xs: 'column',
                        md: 'row',
                    },
                }}
            >
                <ResponsivePanel>{panelSecondary}</ResponsivePanel>
                {isLargeScreen ? <VerticalDivider /> : <FullWidthDivider />}
                <ResponsivePanel>{panelPrimary}</ResponsivePanel>
            </Box>
            <Box
                sx={{
                    textAlign: {xs: 'left', md: 'center'},
                    maxWidth: {xs: PANEL_WIDTH, md: '100%'},
                    marginBottom: theme.spacing(-2),
                }}
            >
                <FullWidthDivider style={{marginBottom: theme.spacing(2)}} />
                {recaptchaAttribution}
            </Box>
        </div>
    );
});
